import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useNavigate, useParams} from '@github-ui/react-router'
import {type MouseEvent, useRef} from 'react'
import {pullRequestsArtifactsRoute} from '../../routes/artifacts-route'
import {usePullRequestArtifacts} from './use-pull-request-artifacts'

export const useSelectedPullRequestArtifact = () => {
  const navigate = useNavigate()
  const params = useRouteParams(pullRequestsArtifactsRoute)
  const {artifact_number: artifactNumber} = useParams<{artifact_number: string}>()
  const {data, isPending, isLoadingError} = usePullRequestArtifacts()
  const artifacts = data?.artifacts ?? []
  const focusedNumber = useRef<number | null>(null)

  const selectedArtifact =
    artifactNumber === undefined ? artifacts[0] : artifacts.find(artifact => artifact.number === Number(artifactNumber))

  const isNotFound = artifactNumber !== undefined && !selectedArtifact
  const getArtifactLinkProps = (number: number) => {
    let linkElement: HTMLAnchorElement | null = null
    const to = `${pullRequestsArtifactsRoute.generatePath(params)}/${number}`
    return {
      to,
      // Primer remounts the link content when its current state changes.
      ref: (element: HTMLAnchorElement | null) => {
        if (!element && linkElement === document.activeElement) focusedNumber.current = number
        linkElement = element
        if (element && focusedNumber.current === number) {
          focusedNumber.current = null
          element.focus()
        }
      },
      onClick: (event: MouseEvent<HTMLAnchorElement>) => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Preserve native modified-link clicks, not keyboard shortcuts.
        const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        if (event.defaultPrevented || event.button !== 0 || isModifiedClick) return
        event.preventDefault()
        if (Number(artifactNumber) === number) return
        navigate(to, {
          replace: artifactNumber === undefined && number === artifacts[0]?.number,
          preventAutofocus: true,
          preventScrollReset: true,
        })
      },
    }
  }

  return {artifacts, isPending, isLoadingError, selectedArtifact, isNotFound, getArtifactLinkProps}
}
